import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { isValidUrl } from "./utils";

export interface AddSiteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (url: string) => Promise<void>;
  isSaving: boolean;
  error: string;
}

export function AddSiteModal({ open, onClose, onSave, isSaving, error }: Readonly<AddSiteModalProps>) {
  const [url, setUrl] = useState("");

  if (!open) return null;

  const handleClose = () => {
    setUrl("");
    onClose();
  };

  const handleSave = () => {
    if (!isValidUrl(url)) return;
    onSave(url);
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">Add Website</h2>
          <button
            onClick={handleClose}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="site-url" className="block text-sm font-medium text-content-secondary mb-1.5">
              Website URL
            </label>
            <input
              id="site-url"
              type="url"
              placeholder="https://example.com/page"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isValidUrl(url)) {
                  handleSave();
                }
              }}
              className={`w-full px-3 py-2 bg-surface-secondary border rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary ${error || (url.trim() && !isValidUrl(url)) ? "border-red-500" : "border-border-secondary"}`}
            />
            {(() => {
              if (error) {
                return <p className="text-xs text-red-400 mt-1.5">{error}</p>;
              }
              if (url.trim() && !isValidUrl(url)) {
                return (
                  <p className="text-xs text-red-400 mt-1.5">Enter a valid URL starting with https:// or http://</p>
                );
              }
              return (
                <p className="text-xs text-content-quaternary mt-1.5">
                  The page content will be fetched, converted to text, and indexed for search.
                </p>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !isValidUrl(url)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? "Adding..." : "Add & Fetch"}
          </button>
        </div>
      </div>
    </div>
  );
}
